'use client';

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { LiveModeStatus } from './useLiveMode';
import { PhoneOff, Mic, Volume2, Settings } from 'lucide-react';
import useAudioLevelStore from '@/store/useAudioLevelStore';
import ShinyText from '@/components/ShinyText';

interface LiveModeOverlayProps {
  status: LiveModeStatus;
  isModelSpeaking: boolean;
  onStartSession?: () => void;
  onEndSession: () => void;
  /** Optional custom element to replace the default Volume2 speaker icon */
  speakerIcon?: React.ReactNode;
  /** Render-prop slot for the settings dropdown content; receives a close() callback */
  settingsSlot?: (close: () => void) => React.ReactNode;
  /** Content shown inside the settings trigger button (replaces default gear icon) */
  settingsTrigger?: React.ReactNode;
  /** Timestamp (ms) when 13-min warning fired. Null = no warning yet. */
  sessionWarningAt?: number | null;
}

/**
 * Animated waveform bars with smooth transitions
 */
function AnimatedWaveform({ level, isActive, color }: { level: number; isActive: boolean; color: 'indigo' | 'emerald' }) {
  const barCount = 5;
  const minHeight = 4;
  const maxHeight = 20;

  const barHeights = useMemo(() => {
    return Array(barCount).fill(0).map((_, i) => {
      if (!isActive || level < 5) return minHeight;
      const baseHeight = minHeight + (level / 100) * (maxHeight - minHeight);
      // Create wave-like pattern from center
      const center = Math.floor(barCount / 2);
      const distanceFromCenter = Math.abs(i - center);
      const waveMultiplier = 1 - distanceFromCenter * 0.15;
      return Math.max(minHeight, baseHeight * waveMultiplier * (0.6 + Math.random() * 0.8));
    });
  }, [level, isActive]);

  const colorClasses = color === 'indigo'
    ? (isActive ? 'bg-[color:var(--primary-500)]' : 'bg-[color:var(--surface-3)]')
    : (isActive && level > 5 ? 'bg-[color:var(--success-500)]' : 'bg-[color:var(--surface-3)]');

  return (
    <div className="flex items-center gap-0.5 h-5">
      {barHeights.map((height, i) => (
        <div
          key={i}
          className={`w-1 rounded-full transition-all duration-100 ease-out ${colorClasses}`}
          style={{
            height: `${height}px`,
            animationDelay: `${i * 50}ms`,
          }}
        />
      ))}
    </div>
  );
}

export function LiveModeOverlay({
  status,
  isModelSpeaking,
  onStartSession,
  onEndSession,
  speakerIcon,
  settingsSlot,
  settingsTrigger,
  sessionWarningAt,
}: LiveModeOverlayProps) {
  const inputAudioLevel = useAudioLevelStore((s) => s.inputLevel);
  const outputAudioLevel = useAudioLevelStore((s) => s.outputLevel);
  const isInactive = status === 'inactive';
  const isConnecting = status === 'connecting';
  const isReconnecting = status === 'reconnecting';
  const isActive = status === 'active';

  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    if (!showSettings) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // 2-minute countdown after 13-min warning fires
  const [countdownSecs, setCountdownSecs] = useState<number | null>(null);
  useEffect(() => {
    if (!sessionWarningAt) { setCountdownSecs(null); return; }
    const tick = () => {
      setCountdownSecs(Math.max(0, Math.round((sessionWarningAt + 120_000 - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionWarningAt]);

  // Show tip bubble when connecting starts
  const [showConnectingTip, setShowConnectingTip] = useState(false);
  const prevStatusRef2 = useRef(status);
  useEffect(() => {
    if (prevStatusRef2.current !== 'connecting' && status === 'connecting') {
      setShowConnectingTip(true);
      const t = setTimeout(() => setShowConnectingTip(false), 5000);
      return () => {
        clearTimeout(t);
        setShowConnectingTip(false);
      };
    }
    prevStatusRef2.current = status;
  }, [status]);

  // Track reconnection success for brief green flash
  const [showReconnected, setShowReconnected] = useState(false);
  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current === 'reconnecting' && status === 'active') {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 2000);
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = status;
  }, [status]);

  return (
    <div className="relative inline-block" ref={settingsRef}>
      {/* Main container */}
      <div
        className={`
          relative inline-flex items-center gap-2.5
          bg-[color:var(--surface-0)]
          border border-[color:var(--surface-3)] rounded-full px-2 py-1
          transition-all duration-300 ease-out
          ${isConnecting || isReconnecting ? 'animate-pulse' : ''}
        `}
      >
        {/* Start session button + Settings gear */}
        {isInactive && onStartSession && (
          <>
            <button
              onClick={onStartSession}
              className="flex items-center gap-3 px-5 py-2.5 rounded-full hover:bg-[color:var(--surface-2)] active:scale-[0.97] transition-all duration-150"
            >
              <Mic className="w-5 h-5 text-[color:var(--primary-500)] shrink-0" />
              <ShinyText
                text="Start Live Tutor"
                color="var(--primary-600)"
                shineColor="var(--primary-300)"
                speed={2.5}
                delay={1}
                className="text-base font-semibold"
              />
            </button>
            {settingsSlot && (
              <button
                onClick={() => setShowSettings((v) => !v)}
                className={`transition-opacity duration-150 ${
                  settingsTrigger
                    ? 'w-[40px] h-[40px] rounded-full overflow-hidden shrink-0'
                    : `p-1.5 rounded-full ${showSettings ? 'bg-[color:var(--primary-100)] text-[color:var(--primary-600)]' : 'text-[color:var(--primary-600)] hover:bg-[color:var(--primary-500)] hover:text-white'}`
                } ${showSettings ? 'opacity-70' : 'opacity-100'}`}
                aria-label="Character settings"
              >
                {settingsTrigger || <Settings className="w-4.5 h-4.5" />}
              </button>
            )}
          </>
        )}

        {/* Connection status */}
        {isConnecting && (
          <div className="flex items-center gap-2.5 py-3">
            <div className="relative">
              <div className="w-2.5 h-2.5 bg-[color:var(--warning-500)] rounded-full animate-ping absolute inset-0 opacity-75" />
              <div className="w-2.5 h-2.5 bg-[color:var(--warning-500)] rounded-full relative" />
            </div>
            <span className="text-sm text-[color:var(--gray-600)] font-medium">Connecting...</span>
            {onEndSession && (
              <button
                onClick={onEndSession}
                className="text-sm text-[color:var(--gray-500)] hover:text-[color:var(--gray-700)] underline ml-1 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {/* Reconnecting status */}
        {isReconnecting && (
          <div className="flex items-center gap-2.5 py-3">
            <div className="relative">
              <div className="w-2.5 h-2.5 bg-[color:var(--warning-500)] rounded-full animate-ping absolute inset-0 opacity-75" />
              <div className="w-2.5 h-2.5 bg-[color:var(--warning-500)] rounded-full relative" />
            </div>
            <span className="text-sm text-[color:var(--gray-600)] font-medium">Reconnecting...</span>
          </div>
        )}

        {/* Active voice chat UI */}
        {isActive && (
          <>
            {/* Live / Reconnected indicator */}
            <div className="flex items-center gap-1.5">
              {showReconnected ? (
                <>
                  <div className="relative">
                    <div className="w-2.5 h-2.5 bg-[color:var(--success-500)] rounded-full animate-ping absolute inset-0 opacity-75" />
                    <div className="w-2.5 h-2.5 bg-[color:var(--success-500)] rounded-full relative" />
                  </div>
                  <span className="text-sm text-[color:var(--success-600)] font-medium">Reconnected</span>
                </>
              ) : (
                <>
                  <div className="relative">
                    <div className={`absolute inset-0 w-2 h-2 rounded-full ${
                      isModelSpeaking
                        ? 'bg-[color:var(--primary-500)] animate-ping'
                        : inputAudioLevel > 15
                          ? 'bg-[color:var(--success-500)] animate-ping'
                          : 'bg-[color:var(--success-500)] animate-pulse'
                    } opacity-75`} />
                    <div className={`relative w-2 h-2 rounded-full transition-colors duration-300 ${
                      isModelSpeaking
                        ? 'bg-[color:var(--primary-500)]'
                        : 'bg-[color:var(--success-500)]'
                    }`} />
                  </div>
                  <span className={`text-xs font-semibold tracking-wide transition-colors duration-300 ${
                    isModelSpeaking ? 'text-[color:var(--primary-600)]' : 'text-[color:var(--success-600)]'
                  }`}>
                    LIVE
                  </span>
                </>
              )}
            </div>

            <div className="h-5 w-px bg-[color:var(--surface-3)] opacity-60" />

            {/* Tutor speaking indicator */}
            <div className="flex items-center gap-2 px-2 py-1 rounded-full">
              {speakerIcon || (
                <Volume2 className={`w-4 h-4 transition-all duration-300 ${
                  isModelSpeaking ? 'text-[color:var(--primary-500)] scale-110' : 'text-[color:var(--gray-400)]'
                }`} />
              )}
              <AnimatedWaveform level={outputAudioLevel} isActive={isModelSpeaking} color="indigo" />
            </div>

            <div className="h-5 w-px bg-[color:var(--surface-3)] opacity-60" />

            {/* User speaking indicator */}
            <div className="flex items-center gap-2 px-2 py-1 rounded-full">
              <Mic className={`w-4 h-4 transition-all duration-300 ${
                !isModelSpeaking && inputAudioLevel > 10 ? 'text-[color:var(--success-500)] scale-110' : 'text-[color:var(--gray-400)]'
              }`} />
              <AnimatedWaveform level={inputAudioLevel} isActive={!isModelSpeaking} color="emerald" />
            </div>

            <div className="h-5 w-px bg-[color:var(--surface-3)] opacity-60" />

            {/* Duration countdown chip */}
            {countdownSecs !== null && countdownSecs > 0 && (
              <>
                <span className="text-xs font-mono font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 tabular-nums">
                  {Math.floor(countdownSecs / 60)}:{String(countdownSecs % 60).padStart(2, '0')}
                </span>
                <div className="h-5 w-px bg-[color:var(--surface-3)] opacity-60" />
              </>
            )}

            {/* End button with hover animation */}
            <button
              onClick={onEndSession}
              className="group flex items-center gap-1.5 px-3 py-1 bg-[color:var(--secondary-50)] hover:bg-[color:var(--error-600)] text-[color:var(--error-600)] hover:text-white rounded-full text-xs font-semibold transition-all duration-200 hover:shadow-md hover:scale-105"
            >
              <PhoneOff className="w-3.5 h-3.5 transition-transform group-hover:rotate-12" />
              <span>End</span>
            </button>
          </>
        )}
      </div>
      {/* Quiet environment tip bubble */}
      <AnimatePresence>
        {showConnectingTip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 6 }}
            transition={{ type: 'spring', stiffness: 520, damping: 30, mass: 0.8 }}
            style={{ transformOrigin: '50% 100%' }}
            className="absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+12px)] bg-white border border-[color:var(--surface-3)] rounded-2xl shadow-xl z-50 px-4 py-2.5 whitespace-nowrap"
          >
            <span className="display-text text-sm text-[color:var(--gray-700)]">
              🎧 Find a quiet spot or use headphones for best results
            </span>
            {/* Bubble tail – outer */}
            <div className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 w-0 h-0
              border-l-[7px] border-l-transparent
              border-r-[7px] border-r-transparent
              border-t-[7px] border-t-[color:var(--surface-3)]" />
            {/* Bubble tail – inner */}
            <div className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-0 h-0
              border-l-[6px] border-l-transparent
              border-r-[6px] border-r-transparent
              border-t-[6px] border-t-white" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Character picker bubble */}
      <AnimatePresence>
        {isInactive && showSettings && settingsSlot && (
          <motion.div
            initial={{ opacity: 0, scale: 0.82, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.82, y: 10 }}
            transition={{ type: 'spring', stiffness: 520, damping: 30, mass: 0.8 }}
            style={{ transformOrigin: '88% 100%' }}
            className="absolute right-0 bottom-[calc(100%+14px)] bg-white border border-[color:var(--surface-3)] rounded-2xl shadow-2xl z-50 min-w-[220px]"
          >
            {settingsSlot(() => setShowSettings(false))}
            {/* Bubble tail – outer (border color) */}
            <div className="absolute -bottom-[7px] right-[30px] w-0 h-0
              border-l-[7px] border-l-transparent
              border-r-[7px] border-r-transparent
              border-t-[7px] border-t-[color:var(--surface-3)]" />
            {/* Bubble tail – inner (white fill) */}
            <div className="absolute -bottom-[6px] right-[31px] w-0 h-0
              border-l-[6px] border-l-transparent
              border-r-[6px] border-r-transparent
              border-t-[6px] border-t-white" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
